import { fileUrl } from '../lib/utils';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
// DropdownMenu no longer needed - using direct action icons
import {
    FileText,
    Plus,
    Search,
    Filter,
    Eye,
    CheckCircle,
    XCircle,
    Clock,
    Upload,
    Download,
    Euro,
    Loader2,
    AlertCircle,
    Trash2,
    X,
    AlertTriangle,
    Flag,
} from 'lucide-react';
import { toast } from 'sonner';

const QuoteRequests = () => {
    const { api, user, isAdmin, isCommercial } = useAuth();
    
    const [requests, setRequests] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('requests');
    
    // Modal states
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBulkActionModal, setShowBulkActionModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');
    const [bulkActionValue, setBulkActionValue] = useState('');
    
    // Upload form
    const [uploadForm, setUploadForm] = useState({
        reference: '',
        amount_ht: '',
        amount_ttc: '',
        notes: '',
        file: null,
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            
            const [requestsRes, quotesRes] = await Promise.all([
                api.get(`/quote-requests?${params.toString()}`),
                api.get('/quotes'),
            ]);
            
            setRequests(requestsRes.data);
            setQuotes(quotesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (requestId, newStatus) => {
        try {
            await api.patch(`/quote-requests/${requestId}`, { status: newStatus });
            toast.success('Statut mis à jour');
            fetchData();
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleUploadQuote = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const formData = new FormData();
            formData.append('quote_request_id', selectedRequest.id);
            if (uploadForm.reference) formData.append('reference', uploadForm.reference);
            if (uploadForm.amount_ht) formData.append('amount_ht', uploadForm.amount_ht);
            if (uploadForm.amount_ttc) formData.append('amount_ttc', uploadForm.amount_ttc);
            if (uploadForm.notes) formData.append('notes', uploadForm.notes);
            if (uploadForm.file) formData.append('file', uploadForm.file);
            
            await api.post('/quotes', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            
            toast.success('Devis déposé avec succès');
            setShowUploadModal(false);
            setUploadForm({
                reference: '',
                amount_ht: '',
                amount_ttc: '',
                notes: '',
                file: null,
            });
            fetchData();
        } catch (error) {
            console.error('Failed to upload quote:', error);
            toast.error('Erreur lors du dépôt du devis');
        } finally {
            setSubmitting(false);
        }
    };

    const handleQuoteStatusChange = async (quoteId, newStatus) => {
        try {
            const updateData = { status: newStatus };
            if (newStatus === 'signed') {
                updateData.signed_at = new Date().toISOString();
            }
            
            await api.patch(`/quotes/${quoteId}`, updateData);
            toast.success('Statut du devis mis à jour');
            fetchData();
        } catch (error) {
            console.error('Failed to update quote status:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const getStatusBadge = (status, processedByName = null) => {
        const styles = {
            draft: 'bg-gray-500/10 text-gray-500',
            submitted: 'bg-blue-500/10 text-blue-500',
            processing: 'bg-orange-500/10 text-orange-500',
            quote_uploaded: 'bg-purple-500/10 text-purple-500',
            closed: 'bg-green-500/10 text-green-500',
            cancelled: 'bg-red-500/10 text-red-500',
        };
        const labels = {
            draft: 'Brouillon',
            submitted: 'Envoyée',
            processing: processedByName ? `En traitement par ${processedByName}` : 'En traitement',
            quote_uploaded: 'Devis déposé',
            closed: 'Clôturée',
            cancelled: 'Annulée',
        };
        return (
            <Badge className={styles[status] || styles.draft}>
                {labels[status] || status}
            </Badge>
        );
    };

    const getQuoteStatusBadge = (status) => {
        const styles = {
            pending: 'bg-blue-500/10 text-blue-500',
            sent: 'bg-purple-500/10 text-purple-500',
            signed: 'bg-green-500/10 text-green-500',
            rejected: 'bg-red-500/10 text-red-500',
            expired: 'bg-gray-500/10 text-gray-500',
        };
        const labels = {
            pending: 'En attente',
            sent: 'Envoyé',
            signed: 'Signé',
            rejected: 'Refusé',
            expired: 'Expiré',
        };
        return (
            <Badge className={styles[status] || styles.pending}>
                {labels[status] || status}
            </Badge>
        );
    };

    // Selection functions
    const toggleSelection = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredRequests.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredRequests.map(r => r.id));
        }
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const openBulkActionModal = (action) => {
        setBulkAction(action);
        setBulkActionValue('');
        setShowBulkActionModal(true);
    };

    const handleDeleteSingle = async (requestId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande de devis ?')) {
            return;
        }
        
        try {
            await api.delete(`/quote-requests/${requestId}`);
            toast.success('Demande de devis supprimée');
            fetchData();
        } catch (error) {
            console.error('Failed to delete:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) return;
        
        setSubmitting(true);
        try {
            if (bulkAction === 'delete') {
                // Delete each request
                let success = 0;
                for (const id of selectedIds) {
                    try {
                        await api.delete(`/quote-requests/${id}`);
                        success++;
                    } catch (e) {
                        console.error('Failed to delete:', id);
                    }
                }
                toast.success(`${success} demande(s) supprimée(s)`);
            } else if (bulkAction === 'change_urgency') {
                // Change urgency for each request
                let success = 0;
                for (const id of selectedIds) {
                    try {
                        await api.patch(`/quote-requests/${id}`, { urgency: bulkActionValue });
                        success++;
                    } catch (e) {
                        console.error('Failed to update:', id);
                    }
                }
                toast.success(`${success} demande(s) mise(s) à jour`);
            }
            
            fetchData();
            clearSelection();
            setShowBulkActionModal(false);
        } catch (error) {
            console.error('Bulk action failed:', error);
            toast.error("Erreur lors de l'action");
        } finally {
            setSubmitting(false);
        }
    };

    const getUrgencyBadge = (urgency) => {
        const styles = {
            low: 'bg-gray-500/10 text-gray-500',
            normal: 'bg-blue-500/10 text-blue-500',
            high: 'bg-orange-500/10 text-orange-500',
        };
        const labels = {
            low: 'Basse',
            normal: 'Normale',
            high: 'Haute',
        };
        return (
            <Badge className={styles[urgency] || styles.normal}>
                {labels[urgency] || urgency}
            </Badge>
        );
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const filteredRequests = requests.filter(r => {
        // Exclude closed requests from main tab
        if (r.status === 'closed') return false;
        if (!searchQuery) return true;
        return r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
               r.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    });
    
    const closedRequests = requests.filter(r => {
        if (r.status !== 'closed') return false;
        if (!searchQuery) return true;
        return r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
               r.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                </div>
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Demandes de devis</h1>
                    <p className="text-muted-foreground mt-1">
                        Gérez les demandes et les devis
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="requests" className="hover:bg-muted transition-colors">
                        Demandes ({requests.filter(r => r.status !== 'closed').length})
                    </TabsTrigger>
                    <TabsTrigger value="closed" className="hover:bg-muted transition-colors">
                        Clôturées ({requests.filter(r => r.status === 'closed').length})
                    </TabsTrigger>
                    <TabsTrigger value="quotes" className="hover:bg-muted transition-colors">
                        Devis ({quotes.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="requests" className="space-y-4 mt-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9 rounded-lg"
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full sm:w-48 rounded-lg">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="submitted">Envoyées</SelectItem>
                                        <SelectItem value="processing">En traitement</SelectItem>
                                        <SelectItem value="quote_uploaded">Devis déposé</SelectItem>
                                        <SelectItem value="closed">Clôturées</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Bulk Actions */}
                    {selectedIds.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                            {selectedIds.length} élément(s) sélectionné(s)
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={clearSelection}
                                            className="h-8 px-2"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openBulkActionModal('change_urgency')}
                                            className="h-8"
                                        >
                                            <Flag className="w-4 h-4 mr-2" />
                                            Changer urgence
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openBulkActionModal('delete')}
                                            className="h-8 text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Supprimer
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Requests Table */}
                    <Card>
                        <CardContent className="p-0">
                            {filteredRequests.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                                    <p className="text-muted-foreground">Aucune demande de devis</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[60px] pl-6">
                                                <Checkbox
                                                    checked={selectedIds.length === filteredRequests.length && filteredRequests.length > 0}
                                                    onCheckedChange={toggleSelectAll}
                                                />
                                            </TableHead>
                                            <TableHead>Demande</TableHead>
                                            <TableHead className="hidden md:table-cell">Entreprise</TableHead>
                                            <TableHead className="hidden lg:table-cell">Demandeur</TableHead>
                                            <TableHead>Urgence</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="w-[60px] pr-6"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredRequests.map((request) => (
                                            <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                                                <TableCell className="pl-6">
                                                    <Checkbox
                                                        checked={selectedIds.includes(request.id)}
                                                        onCheckedChange={() => toggleSelection(request.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{request.title}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {formatDate(request.created_at)}
                                                        </p>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    {request.company_name || '-'}
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    {request.requested_by_name}
                                                </TableCell>
                                                <TableCell>
                                                    {getUrgencyBadge(request.urgency)}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(request.status, request.processed_by_name)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="rounded-full h-8 w-8"
                                                            onClick={() => {
                                                                setSelectedRequest(request);
                                                                setShowDetailModal(true);
                                                            }}
                                                            title="Voir détails"
                                                        >
                                                            <Eye className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                        </Button>
                                                        {isAdmin() && request.status !== 'quote_uploaded' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8"
                                                                onClick={() => {
                                                                    setSelectedRequest(request);
                                                                    setShowUploadModal(true);
                                                                }}
                                                                title="Déposer un devis"
                                                            >
                                                                <Upload className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                            </Button>
                                                        )}
                                                        {isAdmin() && request.status === 'submitted' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8"
                                                                onClick={() => handleStatusChange(request.id, 'processing')}
                                                                title="Marquer en traitement"
                                                            >
                                                                <Clock className="w-4 h-4 text-muted-foreground hover:text-orange-500" />
                                                            </Button>
                                                        )}
                                                        {isAdmin() && request.status !== 'closed' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8"
                                                                onClick={() => handleStatusChange(request.id, 'closed')}
                                                                title="Clôturer"
                                                            >
                                                                <CheckCircle className="w-4 h-4 text-muted-foreground hover:text-green-500" />
                                                            </Button>
                                                        )}
                                                        {isAdmin() && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8 hover:bg-red-500/10"
                                                                onClick={() => handleDeleteSingle(request.id)}
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-red-500" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Closed Requests Tab */}
                <TabsContent value="closed" className="space-y-4 mt-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher dans les clôturées..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9 rounded-lg"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Closed Requests Table */}
                    <Card>
                        <CardContent className="p-0">
                            {closedRequests.length === 0 ? (
                                <div className="text-center py-12">
                                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                                    <p className="text-muted-foreground">Aucune demande clôturée</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Demande</TableHead>
                                            <TableHead className="hidden md:table-cell">Entreprise</TableHead>
                                            <TableHead className="hidden lg:table-cell">Demandeur</TableHead>
                                            <TableHead>Clôturée le</TableHead>
                                            <TableHead className="w-[100px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {closedRequests.map((request) => (
                                            <TableRow key={request.id} data-testid={`closed-request-row-${request.id}`}>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{request.title}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            Créée le {formatDate(request.created_at)}
                                                        </p>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    {request.company_name || '-'}
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    {request.requested_by_name}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(request.updated_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="rounded-full h-8 w-8 hover:bg-muted transition-colors"
                                                            onClick={() => {
                                                                setSelectedRequest(request);
                                                                setShowDetailModal(true);
                                                            }}
                                                            title="Voir détails"
                                                            data-testid={`closed-view-${request.id}`}
                                                        >
                                                            <Eye className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="rounded-full h-8 w-8 hover:bg-muted transition-colors"
                                                            onClick={() => handleStatusChange(request.id, 'submitted')}
                                                            title="Dé-clôturer"
                                                            data-testid={`closed-reopen-${request.id}`}
                                                        >
                                                            <XCircle className="w-4 h-4 text-muted-foreground hover:text-orange-500" />
                                                        </Button>
                                                        {isAdmin() && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8 hover:bg-red-500/10"
                                                                onClick={() => handleDeleteSingle(request.id)}
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-red-500" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="quotes" className="space-y-4 mt-4">
                    {/* Quotes Table */}
                    <Card>
                        <CardContent className="p-0">
                            {quotes.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                                    <p className="text-muted-foreground">Aucun devis</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Référence</TableHead>
                                            <TableHead>Montant HT</TableHead>
                                            <TableHead className="hidden md:table-cell">Déposé par</TableHead>
                                            <TableHead className="hidden lg:table-cell">Date</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="w-[50px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {quotes.map((quote) => (
                                            <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                                                <TableCell>
                                                    <p className="font-medium">{quote.reference || 'Sans référence'}</p>
                                                    {quote.file_name && (
                                                        <p className="text-sm text-muted-foreground">{quote.file_name}</p>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <p className="font-medium">{formatCurrency(quote.amount_ht)}</p>
                                                    {quote.amount_ttc && (
                                                        <p className="text-sm text-muted-foreground">
                                                            TTC: {formatCurrency(quote.amount_ttc)}
                                                        </p>
                                                    )}
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    {quote.uploaded_by_name}
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    {formatDate(quote.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    {getQuoteStatusBadge(quote.status)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        {quote.file_url && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="rounded-full h-8 w-8"
                                                                asChild
                                                                title="Télécharger"
                                                                data-testid={`quote-download-${quote.id}`}
                                                            >
                                                                <a href={fileUrl(quote.file_url)} target="_blank" rel="noopener noreferrer">
                                                                    <Download className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                                </a>
                                                            </Button>
                                                        )}
                                                        {isCommercial() && quote.status === 'pending' && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="rounded-full h-8 w-8"
                                                                    onClick={() => handleQuoteStatusChange(quote.id, 'signed')}
                                                                    title="Marquer signé"
                                                                    data-testid={`quote-sign-${quote.id}`}
                                                                >
                                                                    <CheckCircle className="w-4 h-4 text-muted-foreground hover:text-green-500" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="rounded-full h-8 w-8"
                                                                    onClick={() => handleQuoteStatusChange(quote.id, 'rejected')}
                                                                    title="Marquer refusé"
                                                                    data-testid={`quote-reject-${quote.id}`}
                                                                >
                                                                    <XCircle className="w-4 h-4 text-muted-foreground hover:text-red-500" />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Détails de la demande</DialogTitle>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-muted-foreground">Objet</Label>
                                <p className="font-medium">{selectedRequest.title}</p>
                            </div>
                            {selectedRequest.description && (
                                <div>
                                    <Label className="text-muted-foreground">Description</Label>
                                    <p>{selectedRequest.description}</p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Entreprise</Label>
                                    <p className="font-medium">{selectedRequest.company_name || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Support</Label>
                                    <p className="font-medium">{selectedRequest.support || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Montant total (€ HT)</Label>
                                    <p className="font-medium">{formatCurrency(selectedRequest.estimated_amount)}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Date souhaitée</Label>
                                    <p className="font-medium">{formatDate(selectedRequest.desired_date)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Urgence</Label>
                                    <div className="mt-1">{getUrgencyBadge(selectedRequest.urgency)}</div>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Statut</Label>
                                    <div className="mt-1">{getStatusBadge(selectedRequest.status, selectedRequest.processed_by_name)}</div>
                                </div>
                            </div>
                            {selectedRequest.notes && (
                                <div>
                                    <Label className="text-muted-foreground">Notes</Label>
                                    <p>{selectedRequest.notes}</p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Upload Quote Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Déposer un devis</DialogTitle>
                        <DialogDescription>
                            Pour la demande : {selectedRequest?.title}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUploadQuote} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Référence du devis</Label>
                            <Input
                                value={uploadForm.reference}
                                onChange={(e) => setUploadForm({ ...uploadForm, reference: e.target.value })}
                                placeholder="DEV-2024-001"
                                className="rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Montant HT (€)</Label>
                                <Input
                                    type="number"
                                    value={uploadForm.amount_ht}
                                    onChange={(e) => setUploadForm({ ...uploadForm, amount_ht: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Montant TTC (€)</Label>
                                <Input
                                    type="number"
                                    value={uploadForm.amount_ttc}
                                    onChange={(e) => setUploadForm({ ...uploadForm, amount_ttc: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Fichier PDF</Label>
                            <Input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={uploadForm.notes}
                                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                                rows={3}
                                className="rounded-lg"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowUploadModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Déposer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Bulk Action Modal */}
            <Dialog open={showBulkActionModal} onOpenChange={setShowBulkActionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {bulkAction === 'delete' ? 'Supprimer les demandes' : 'Changer l\'urgence'}
                        </DialogTitle>
                        <DialogDescription>
                            {bulkAction === 'delete' 
                                ? `Êtes-vous sûr de vouloir supprimer ${selectedIds.length} demande(s) ?`
                                : `Changer l'urgence de ${selectedIds.length} demande(s)`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    {bulkAction === 'change_urgency' && (
                        <div className="space-y-2">
                            <Label>Nouvelle urgence</Label>
                            <Select value={bulkActionValue} onValueChange={setBulkActionValue}>
                                <SelectTrigger className="rounded-lg hover:bg-muted transition-colors">
                                    <SelectValue placeholder="Sélectionner l'urgence" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Basse</SelectItem>
                                    <SelectItem value="normal">Normale</SelectItem>
                                    <SelectItem value="high">Haute</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    
                    {bulkAction === 'delete' && (
                        <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                Cette action est irréversible. Les demandes supprimées ne pourront pas être récupérées.
                            </AlertDescription>
                        </Alert>
                    )}
                    
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowBulkActionModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleBulkAction}
                            disabled={submitting || (bulkAction === 'change_urgency' && !bulkActionValue)}
                            className={`rounded-full ${bulkAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : bulkAction === 'delete' ? (
                                'Supprimer'
                            ) : (
                                'Modifier'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default QuoteRequests;
